import React, { useCallback, useState, createContext, useContext } from 'react';
import type { TagData } from '../components/TagInput';
import { subscriptions as mockSubscriptions, availableTags as mockAvailableTags, PaymentInfo, CardExpiryInfo } from '../data/mockSubscriptionData';
export interface Subscription {
  id: string;
  status: string;
  customerName: string;
  customer: string;
  location: string;
  amount: string;
  subscriptionTitle: string;
  discountPercent: number;
  nextPayment: string;
  // Pack billing schedule; missing means Rolling
  billingSchedule?: 'rolling' | 'fixedDay' | 'releaseDates';
  startDate: string;
  endDate?: string;
  isSkipped?: boolean;
  skipCount?: number;
  orderCount: number;
  tags: string[];
  paymentInfo?: PaymentInfo;
  cardExpiry?: CardExpiryInfo;
  items: Array<{
    quantity: number;
    name: string;
  }>;
}
interface AppDataContextType {
  // Subscriptions
  subscriptions: Subscription[];
  getSubscriptionById: (id: string) => Subscription | undefined;
  updateSubscription: (id: string, updates: Partial<Subscription>) => void;
  createSubscription: (subscription: Omit<Subscription, 'id'>) => string;
  // Tags
  allTags: TagData[];
  createTag: (label: string, color: TagData['color']) => TagData;
  getSubscriptionTags: (subscriptionId: string) => TagData[];
  addTagToSubscription: (subscriptionId: string, tag: TagData) => void;
  removeTagFromSubscription: (subscriptionId: string, tagId: string) => void;
}
const AppDataContext = createContext<AppDataContextType | undefined>(undefined);
export function AppDataProvider({
  children
}: {
  children: React.ReactNode;
}) {
  // State
  const [subscriptions, setSubscriptions] = useState<Subscription[]>(mockSubscriptions);
  const [allTags, setAllTags] = useState<TagData[]>(mockAvailableTags);
  // Subscriptions methods
  const getSubscriptionById = useCallback((id: string) => {
    return subscriptions.find(sub => sub.id === id);
  }, [subscriptions]);
  const updateSubscription = useCallback((id: string, updates: Partial<Subscription>) => {
    setSubscriptions(prev => prev.map(sub => sub.id === id ? {
      ...sub,
      ...updates
    } : sub));
  }, []);
  const createSubscription = useCallback((subscription: Omit<Subscription, 'id'>): string => {
    // Generate a new subscription ID
    const newId = `SUB-${Math.floor(Math.random() * 90000) + 10000}`;
    const newSubscription: Subscription = {
      ...subscription,
      id: newId
    };
    setSubscriptions(prev => [newSubscription, ...prev]);
    return newId;
  }, []);
  // Tags methods
  const createTag = useCallback((label: string, color: TagData['color']): TagData => {
    const newTagId = label.toLowerCase().replace(/\s+/g, '-');
    const newTag: TagData = {
      id: newTagId,
      label,
      color
    };
    setAllTags(prev => [...prev, newTag]);
    return newTag;
  }, []);
  const getSubscriptionTags = useCallback((subscriptionId: string): TagData[] => {
    const subscription = subscriptions.find(sub => sub.id === subscriptionId);
    if (!subscription) return [];
    return subscription.tags.map(tagId => allTags.find(t => t.id === tagId)).filter((tag): tag is TagData => tag !== undefined);
  }, [subscriptions, allTags]);
  const addTagToSubscription = useCallback((subscriptionId: string, tag: TagData) => {
    setSubscriptions(prev => prev.map(sub => {
      if (sub.id === subscriptionId) {
        // Don't add if already exists
        if (sub.tags.includes(tag.id)) return sub;
        return {
          ...sub,
          tags: [...sub.tags, tag.id]
        };
      }
      return sub;
    }));
  }, []);
  const removeTagFromSubscription = useCallback((subscriptionId: string, tagId: string) => {
    setSubscriptions(prev => prev.map(sub => {
      if (sub.id === subscriptionId) {
        return {
          ...sub,
          tags: sub.tags.filter(t => t !== tagId)
        };
      }
      return sub;
    }));
  }, []);
  const value: AppDataContextType = {
    subscriptions,
    getSubscriptionById,
    updateSubscription,
    createSubscription,
    allTags,
    createTag,
    getSubscriptionTags,
    addTagToSubscription,
    removeTagFromSubscription
  };
  return <AppDataContext.Provider value={value} data-id="element-6352" data-mp="1or5fre5zlw7q">{children}</AppDataContext.Provider>;
}
// Custom hook to use the context
export function useAppData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
}